<template>
  <div class="uploader" :class="{'in-modal': modal}">
    <CRow>
      <CCol>
        <template v-if="!modal">
          <div class="d-flex align-items-start justify-content-between mb-4">
            <div>
              <h1 class="main-header mb-1" v-translate translate-context="uploader">
                Upload media
              </h1>
              <translate translate-context="uploader">
                Insert a customized gallery of photos and videos visible to all participants
              </translate>
            </div>
          </div>
        </template>
        <template v-if="media_upload_left > 0">
          <span v-if="media_upload_left < 999">{{ media_upload_left }} media available</span>
          <PFileDrop
              @dropped="newFileDropped"
              :disabled="!canUploadFiles"
              ref="fileDrop"
              multiple
              class="p-file-drop mb-4"
          />
        </template>
        <template v-else>
          You used all the media available for your plan
        </template>
        <CAlert color="danger" class="alert-uploader alert-uploader--danger" :show.sync="showFailedBanner" close-button>
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <CIcon name="cipTriangleWarning" size="lg" class="mr-2"/>
              <translate :translate-params="{num: failed.length}">Error: %{num} file failed</translate>
            </div>
            <CButton variant="outline" color="danger" v-translate translate-context="uploader"
                     @click.stop="openNotification('FAILED')">Detail
            </CButton>
          </div>
        </CAlert>
        <CAlert color="info" class="alert-uploader alert-uploader--info" :show.sync="showCompletedBanner" close-button>
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <CIcon name="cipCircleCheck" size="lg" class="mr-2"/>
              <translate :translate-params="{num: completed.length}">%{num} file uploaded</translate>
            </div>
            <CButton color="primary" variant="outline" v-translate translate-context="uploader"
                     @click.stop="openNotification('COMPLETED')">Detail
            </CButton>
          </div>
        </CAlert>
      </CCol>
    </CRow>
    <CRow :class="{'invisible-wrapper': !showFileTable}">
      <CCol sm="12" v-if="isAdvanced">
        <PTabs :tabs="tabs" :selected="tab" @select="tab = $event" pills/>
      </CCol>
      <CCol sm="12" class="mt-3">
        <FileTable
            v-if="tab === tabs.TO_UPLOAD.name"
            :rows="toUpload"
            @delete="deleteFileFromToUpload"
            @selected="selected = $event"
            @loadImage="onLoadImage"
        />
        <FileTable
            v-if="tab === tabs.IN_PROGRESS.name"
            :rows="inProgress"
            @delete="abortUpload"
            @selected="selected = $event"
            @loadImage="onLoadImage"
        />
      </CCol>
    </CRow>
    <div class="uploader__bar">
      <CRow>
        <CCol>
          <div class="d-flex justify-content-between align-items-center p-2">
            <div class="d-flex align-items-center">
              <PDropDown
                  color="secondary"
                  :items="actions"
                  v-if="actions.length > 0"
                  :button-if-single-item="true"
                  :reset-on-select="true"
                  :empty-label="$pgettext('uploader', 'Action')"
                  @change="onActionChange"
              />
              <div v-if="selectedCount > 0" class="ml-3">
                <translate :translate-n="selectedCount" translate-plural="%{ selectedCount } files selected">%{
                  selectedCount } file selected
                </translate>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <UploaderStats
                  :is-in-progress="tab === tabs.IN_PROGRESS.name"
                  :in-progress="inProgress"
                  :to-upload="toUpload"
                  :completed="completed"
                  class="ml-3 d-none d-md-block"
              />
              <Notifications
                  ref="notifications"
                  :can-see-photo-search="canSeePhotoSearch"
                  :completed="completed"
                  :pending="pending"
                  :failed="failed"
                  @delete-completed="deleteFromCompleted"
                  @delete-failed="deleteFromFailed"
                  @delete-pending="deleteFromPending"
                  class="ml-3"
                  v-c-clickaway="hideNotifications"
              />
              <CButton
                  :disabled="!readyToUpload"
                  @click="openSummary"
                  size="sm"
                  color="primary"
                  v-translate translate-context="uploader"
                  class="ml-3"
              >Upload
              </CButton>

            </div>
          </div>
        </CCol>
      </CRow>
    </div>


    <Summary
        ref="summary"
        @confirm="onConfirmUpload"
    />
    <TagSelector
        :available_tags="availableTags"
        @update="onAddTags"
        ref="tagSelector"
    />
    <GallerySelector
        :available_galleries="availableGalleries"
        @update="onSetGallery"
        ref="gallerySelector"
    />
    <PicaCodeSelector
        @update="onSetPicaCode"
        ref="picaCodeSelector"
    />
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import PFileDrop from "@/domain/core/components/upload/PFileDrop.vue";
import {UploadScheduler} from "@/domain/uploader/scheduler";
import Notifications from "@/domain/uploader/components/Notifications.vue";
import PDropDown from "@/domain/core/components/PDropDown.vue";
import TagSelector from "@/domain/uploader/components/TagSelector.vue";
import PicaCodeSelector from "@/domain/uploader/components/PicaCodeSelector.vue";
import FileTable from "@/domain/uploader/components/FileTable.vue";
import Summary from "@/domain/uploader/components/Summary.vue";
import UploaderStats from "@/domain/uploader/components/UploaderStats.vue";
import PTabs from "@/domain/core/components/PTabs.vue";
import GallerySelector from "@/domain/uploader/components/GallerySelector.vue";

export default {
  name: 'Uploader',
  components: {
    GallerySelector,
    PTabs,
    UploaderStats, Summary, FileTable, PicaCodeSelector, TagSelector, PDropDown, Notifications, PFileDrop
  },
  data() {
    return {
      scheduler: null,
      tab: null,
      showFailedBanner: false,
      showCompletedBanner: false,
      selected: [],
      inUpload: false
    }
  },
  props: {
    modal: {
      type: Boolean,
      default: false
    },
    gallery: {
      type: [Number, String],
      default: null,
      required: false
    }
  },
  beforeDestroy() {
    this.inUpload = false
  },
  async mounted() {

    this.setPhotographerId(this.user.id)
    this.setPicaServiceId(this.$route.params.picaServiceId)
    this.setGalleryId(this.gallery)
    if (!this.gallery)
      this.setForceToPublic(false)

    await this.fetchConfig()
    this.restoreStateFromMemory()

    this.scheduler = new UploadScheduler({
      getNewFile: () => {
        return this.inProgress.filter(file => !file.inProgress)[0] ?? null
      },
      onFailure: (file, motivation) => {
        this.moveFilesToFailed({files: [file], motivation: motivation})
      },
      onSuccess: (file) => {
        this.moveFilesToCompleted([file])
        this.onUploadComplete()
      },
      onProgress: (md5, progress, speed) => {
        this.updateFileProgress({md5, progress, speed})
      }
    })
    this.tab = this.tabs.TO_UPLOAD.name
    this.warnUserOnBrowserClose()


    let promises = []
    if (this.hasPerm('uploader.set_pica_code'))
      promises.push(this.fetchAvailableTags())
    if (this.hasPerm('uploader.set_gallery'))
      promises.push(this.fetchAvailableGalleries())

    if (promises.length) {
      await Promise.all([
        this.fetchAvailableTags(),
        this.fetchAvailableGalleries()
      ])
    }
  },
  watch: {
    question(warning_on_close) {
      if (warning_on_close) {
        this.$root.$emit('bv::show::modal', 'warning_on_close')
      } else {
        this.$root.$emit('bv::hide::modal', 'warning_on_close')
      }
    },
    failed(failed) {
      // showFailedBanner it's a flag and not a computed property because we want to allow a user to "ack" the banner
      // when failed number change, we want to show it again
      // if the failed length is 0, we will hide it in any case to avoid confusion
      this.showFailedBanner = failed.length > 0
    },
  },
  computed: {
    ...mapGetters('eventWizard', [
      'canSeePhotoSearch'
    ]),
    ...mapGetters('uploader', [
      'toUpload',
      'inProgress',
      'completed',
      'failed',
      'pending',
      'availableTags',
      'availableGalleries',
      'media_upload_left',
    ]),
    ...mapGetters('event', [
      'isPromo',
    ]),
    ...mapGetters('login', [
      'user'
    ]),
    isAdvanced() {
      return this.hasPerm('uploader.advanced') && !this.modal
    },
    isTaggingAvailable() {
      return this.isAdvanced && this.hasPerm('uploader.set_tag')
    },
    isGalleryAvailable() {
      return this.availableGalleries.length > 0 && this.isAdvanced  && this.hasPerm('uploader.set_gallery')
    },
    readyToUpload() {
      if (this.tab !== this.tabs.TO_UPLOAD.name) return false
      return this.toUpload.length > 0
    },
    actions() {
      let options = []
      if (this.tab === this.tabs.TO_UPLOAD.name) {
        if (this.toUpload.length <= 0) return []

        if (this.isTaggingAvailable) {
          options.push({value: 'update_tags', label: this.$pgettext('uploader.action', 'Add Tag')})
          options.push({value: 'delete_tags', label: this.$pgettext('uploader.action', 'Delete Tag')})
          options.push({separator: true})
        }
        if (this.isGalleryAvailable) {
          options.push({value: 'set_gallery', label: this.$pgettext('upload.action', 'Set Gallery')})
          options.push({separator: true})
        }
        if (this.isAdvanced) {
          options.push({value: 'set_picacode', label: this.$pgettext('upload.action', 'Set Picacode')})
          options.push({value: 'remove_picacode', label: this.$pgettext('upload.action', 'Remove Picacode')})
          if (!this.isPromo) {
            options.push({value: 'set_free', label: this.$pgettext('upload.action', 'Set as Free')})
            options.push({value: 'remove_free', label: this.$pgettext('upload.action', 'Reset is Free')})
          }
          options.push({value: 'set_autotag_disable', label: this.$pgettext('upload.action', 'Disable Autotag')})
          options.push({value: 'remove_autotag_disable', label: this.$pgettext('upload.action', 'Re-Enable Autotag')})
          options.push({separator: true})
        }
        options.push({value: 'delete', label: this.$pgettext('upload.action', 'Delete')})
      } else if (this.tab === this.tabs.IN_PROGRESS.name) {
        if (this.inProgress.length <= 0) return []

        options.push({value: 'delete', label: this.$pgettext('upload.action', 'Delete')})
      }
      return options;
    },
    tabs() {
      return {
        TO_UPLOAD: {
          name: 'to_upload',
          label: this.generateLabel('To upload', this.toUpload.length),
        },
        IN_PROGRESS: {
          name: 'in_progress',
          label: this.generateLabel('In progress', this.inProgress.length),
        },
      }
    },
    showFileTable() {
      return this.toUpload.length > 0 || this.inProgress.length > 0 || this.isAdvanced
    },
    warning_on_close() {
      return this.toUpload.length > 0 || this.inProgress.length > 0
    },
    canUploadFiles() {
      return this.isAdvanced || this.inProgress.length <= 0
    },
    selectedCount() {
      return this.selected.length
    }
  },
  methods: {
    ...mapActions('uploader', [
      'fetchConfig',
      'addFilesToToUpload',
      'deleteFileFromToUpload',
      'updateToUploadTag',
      'updateToUploadPicaCode',
      'updateToUploadGallery',
      'updateToUploadIsFree',
      'updateToUploadDisableAutotag',

      'moveFilesToInProgress',
      'deleteFileFromInProgress',
      'updateFileProgress',

      'moveFilesToCompleted',
      'deleteFromCompleted',

      'moveFilesToFailed',
      'deleteFromFailed',

      'deleteFromPending',

      'setPhotographerId',
      'setPicaServiceId',
      'setGalleryId',
      'fetchAvailableTags',
      'fetchAvailableGalleries',

      'restoreStateFromMemory',

      'updateImageResolution',

      'fetchStats',

      'setForceToPublic'
    ]),
    async onConfirmUpload(files) {
      await this.moveFilesToInProgress(files)

      // When we start a new upload we will "reset" the completed so to not generate a wrong in progress completed status
      // If we have advanced mode active, we can manage multiple session, so it's not needed/wanted
      if (!this.isAdvanced)
        await this.deleteFromCompleted(this.completed)

      this.scheduler.start()
      this.tab = this.tabs.IN_PROGRESS.name
    },
    async newFileDropped(files) {
      const result = await this.addFilesToToUpload(files)
      if (result.errors > 0) {
        const errorMsg = this.$pgettext('uploader', '%{num} files are not valid')
        await this.notifyError(this.$gettextInterpolate(errorMsg, {num: result.errors}))
      }
      if (result.valid > 0)
        this.tab = this.tabs.TO_UPLOAD.name
    },
    warnUserOnBrowserClose() {
      window.addEventListener('beforeunload', (event) => {
        if (!this.warning_on_close) return undefined
        event.preventDefault()
      })
    },
    onLoadImage({img, file}) {
      if (!file.resolution)
        this.updateImageResolution({img, file})
    },
    onUploadComplete() {
      if (this.inProgress.length > 0 || this.toUpload.length > 0) return
      this.showCompletedBanner = true
      this.openNotification('COMPLETED')
    },
    openNotification(type) {
      if (!this.$refs.notifications) return
      this.$refs.notifications.toggleNotifier(type)
    },
    hideNotifications() {
      if (!this.$refs.notifications) return
      this.$refs.notifications.show = false
    },
    openSummary() {
      this.$refs.summary.open(this.toUpload)
    },
    onActionChange(action) {
      if (this.selected.length <= 0)
        return this.notifyError(this.$pgettext('uploader', 'Please select at least one file'))

      if (action === 'delete_tags')
        this.updateToUploadTag({mode: 'delete', files: this.selected})
      else if (action === 'update_tags')
        this.$refs.tagSelector.open()
      else if (action === 'set_gallery')
        this.$refs.gallerySelector.open()
      else if (action === 'set_picacode')
        this.$refs.picaCodeSelector.open()
      else if (action === 'remove_picacode')
        this.updateToUploadPicaCode({picaCode: null, files: this.selected})
      else if (action === 'set_free')
        this.updateToUploadIsFree({isFree: true, files: this.selected})
      else if (action === 'remove_free')
        this.updateToUploadIsFree({isFree: false, files: this.selected})
      else if (action === 'set_autotag_disable')
        this.updateToUploadDisableAutotag({disabled: true, files: this.selected})
      else if (action === 'remove_autotag_disable')
        this.updateToUploadDisableAutotag({disabled: false, files: this.selected})
      else if (action === 'delete') {
        if (this.tab === this.tabs.TO_UPLOAD.name)
          this.deleteFileFromToUpload(this.selected)
        else if (this.tab === this.tabs.IN_PROGRESS.name)
          this.abortUpload(this.selected)
      }
    },
    onAddTags(tags) {
      this.updateToUploadTag({mode: 'add', files: this.selected, tags: tags})
    },
    onSetGallery(gallery) {
      this.updateToUploadGallery({gallery: gallery, files: this.selected})
    },
    onSetPicaCode(picaCode) {
      this.updateToUploadPicaCode({picaCode: picaCode, files: this.selected})
    },
    generateLabel(tab, num) {
      let label = this.$pgettext('uploader', tab)
      if (num > 0)
        label += ' (' + num + ')'
      return label
    },
    abortUpload(files) {
      for (let i = 0; i < files.length; i++)
        this.scheduler.abortUpload(files[i])
      this.deleteFileFromInProgress(files)
    },
    openUploadForm(cbOnFileSelected) {
      this.$refs.fileDrop.open(cbOnFileSelected)
    }
  }
}
</script>